import React from 'react'
import { Text, XStack, YStack, styled } from 'tamagui'
import { Trans, useLingui } from '@lingui/react/macro'

import { useScreenMessages } from '@data/i18n'
import { Metadata } from '@newComponents/Metadata'
import { EmojiIcon } from '@newComponents/EmojiIcon/EmojiIcon'
import { H1, H2, H3, Strong, Paragraph } from '@newComponents/Text'
import { GradientBackgroundScreen } from '@newComponents/Screen'
import { Image } from '@newComponents/Image'
import { Card } from '@newComponents/Card'
import { ExternalLink, Link } from '@newComponents/Link'
import clickProfile from './images/meetup-click-profile.png'
import selectGroup from './images/meetup-select-group.png'
import selectGuilds from './images/guild-select-guilds.jpg'
import selectSettings from './images/guild-guilds-settings.jpg'
import meetupSettings from './images/guild-meetup-settings.jpg'

const StyledImage = ({ src }: { src: string }) => {
  return (
    <XStack justifyContent='center'>
      <Image
        src={src}
        width='100%'
        maxWidth={500}
        borderWidth={1}
        borderColor='$borderColor'
      />
    </XStack>
  )
}

export const MeetupIntegrationScreen = () => {
  const { t } = useLingui()
  useScreenMessages(
    (locale) =>
      import(
        /* webpackChunkName: "locale-meetup-integration-screen-[request]" */ `./locales/${locale}.po`
      )
  )

  return (
    <>
      <Metadata title={t`Meetup Integration`} />

      <GradientBackgroundScreen alignItems='center'>
        <YStack gap='$7'>
          <Section>
            <H1>
              <Trans>Meetup Integration</Trans>
            </H1>
            <H2>
              <Trans>Why Guild?</Trans>
            </H2>
            <Paragraph>
              <Trans>
                Guild is a platform that has been meticulously crafted with
                communities at the forefront.
              </Trans>
            </Paragraph>
            <Paragraph>
              <Trans>
                It&apos;s completely free! You can organize{' '}
                <Link href='/events'>Events</Link> (in-person/online/both), list{' '}
                <Link href='/presentations'>Presentations</Link>, and join
                Networks.
              </Trans>
            </Paragraph>
            <YStack gap='$1'>
              <Paragraph>
                <Trans>You can connect your events with others.</Trans>
              </Paragraph>
              <Paragraph>
                <Trans>
                  For example: Are you hosting a JavaScript event in your local
                  town? With our Networks features, you can connect with
                  organisers in other cities and promote your event across
                  multiple places.
                </Trans>
              </Paragraph>
            </YStack>
            <Paragraph>
              <Trans>
                You can collect Donations and charge for Tickets. Guild charges
                a simple transaction fee of 5% on top of payment processing
                fees.
              </Trans>
            </Paragraph>
          </Section>

          <Section>
            <H2>
              <Trans>Features</Trans>
            </H2>

            <Paragraph fontWeight='$5'>
              <Trans>
                This integration&apos;s purpose is to copy events from Meetup to
                Guild.
              </Trans>
            </Paragraph>

            <Paragraph>
              <Trans>
                Meetup provides limited tooling for this purpose, and this is
                the only method of data export/integration possible short of an
                expensive Pro plan.
              </Trans>
            </Paragraph>

            <Paragraph>
              <Trans>
                If you have a Meetup Pro plan and would like to register your
                interest in a tighter integration, let us know via email at{' '}
                <span className='notranslate'>
                  <ExternalLink href='mailto:hi@guild.host'>
                    hi@guild.host
                  </ExternalLink>
                </span>
                .
              </Trans>
            </Paragraph>
          </Section>

          <Section>
            <H2>
              <Trans>How to set up the Meetup Integration</Trans>
            </H2>
            <H3>
              <Trans>Step 1: Retrieve your group on Meetup</Trans>
            </H3>
            <Paragraph>
              <Trans>
                Sign in to Meetup, then go to <Strong>Your groups</Strong> by
                clicking on your profile picture (or initials) on the right hand
                side of the screen, a drop-down menu will appear, select Your
                groups.
              </Trans>
            </Paragraph>

            <StyledImage src={clickProfile} />

            <Paragraph>
              <Trans>Select the group you want to migrate to Guild.</Trans>
            </Paragraph>

            <StyledImage src={selectGroup} />

            <Paragraph>
              <Trans>Copy the URL, go back to Guild.</Trans>
            </Paragraph>
          </Section>

          <Section>
            <H3>
              <Trans>Step 2: Add your group to Guild Settings Page</Trans>
            </H3>

            <Paragraph>
              <Trans>
                To access your events, go to your profile section, by clicking
                on your avatar.
              </Trans>
            </Paragraph>
            <Paragraph>
              <Trans>
                Click on the Guilds section on the left of your screen, right
                underneath your picture.
              </Trans>
            </Paragraph>
            <StyledImage src={selectGuilds} />
            <Paragraph>
              <Trans>
                Click on your Guild Your events will appear in the centre of the
                screen, click on the one want to modify.
              </Trans>
            </Paragraph>
            <Paragraph>
              <Trans>
                Click on the &apos;settings&apos; button underneath your
                event&apos;s picture on the left.
              </Trans>
            </Paragraph>
            <StyledImage src={selectSettings} />
            <Paragraph>
              <Trans>
                A new page will open with the event settings section on the
                left.
              </Trans>
            </Paragraph>

            <Paragraph>
              <Trans>
                When you&apos;re on the Settings page, click on the
                &apos;Meetup&apos; section on the left hand side of the screen.
                This section will allow you to integrate your Meetup events with
                Guild.
              </Trans>
            </Paragraph>
            <StyledImage src={meetupSettings} />
            <Paragraph>
              <Trans>
                Once you&apos;re on the Meetup integration page, you&apos;ll be
                asked to enter your Meetup id.
              </Trans>
            </Paragraph>
            <Paragraph>
              <Trans>
                Paste in the Meetup URL, then find your meetup group URL, which
                comes after meetup.com. For example, for{' '}
                <Strong>https://www.meetup.com/your-group-id/</Strong>, the URL
                Name would be <Strong>your-group-id</Strong>. Click on the
                &apos;Save&apos; button.
              </Trans>
            </Paragraph>
            <Paragraph>
              <Trans>
                And you&apos;re done! Guild will automatically update the data
                of any upcoming Meetup event you hold with this group, and
                create the same event on Guild.
              </Trans>
            </Paragraph>
          </Section>

          <Section>
            <H3>
              <Trans>Step 3: Manual updates</Trans>
            </H3>
            <YStack gap='$1'>
              <Paragraph>
                <Trans>
                  There are some information that we can&apos;t retrieve from
                  Meetup, so for each event you&apos;ll need to manually update:
                </Trans>
              </Paragraph>
              <Text>
                <Trans>- Description</Trans>
              </Text>
              <Text>
                <Trans>- Address Capacity/Attendance Limit</Trans>
              </Text>
              <Text>
                <Trans>- Virtual URL Event</Trans>
              </Text>
              <Text>
                <Trans>- Cover Photo</Trans>
              </Text>
            </YStack>
          </Section>

          <Section>
            <Paragraph>
              <Trans>
                We&apos;re always available at{' '}
                <ExternalLink href='mailto:hi@guild.host'>
                  hi@guild.host
                </ExternalLink>{' '}
                if you need anything
              </Trans>
            </Paragraph>

            <XStack gap='$1'>
              <Paragraph>
                <Trans>Looking forward to seeing what you get up to!</Trans>
              </Paragraph>
              <EmojiIcon name='red-heart'></EmojiIcon>
            </XStack>
          </Section>
        </YStack>
      </GradientBackgroundScreen>
    </>
  )
}

export default MeetupIntegrationScreen

const Section = styled(Card, {
  tag: 'section',
  gap: '$6',
  maxWidth: 600,
  width: '100%',
})
